import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import type { Coincidence, JoinReport } from '@/lib/crossSystem';
import { CROSS_ENGINE_VERSION } from '@/lib/crossSystem';
import { formatCount, formatDelta, formatGrosze, formatRange } from '@/lib/format';
import { DataQualityBadge } from './states';
import { MetricBasisChips } from './MetricBasisChips';

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', py: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Zbieżność sprzedaży i dostaw. Karta jest tak zbudowana, żeby NIE DAŁO SIĘ przeczytać jej
 * jako wyjaśnienia przyczynowego: fakt, kontekst dostaw, jawne zastrzeżenie, proponowany test.
 */
export function CoincidenceCard({ item, window, comparison, kartaId }: {
  item: Coincidence;
  window: { date_from: string; date_to: string };
  comparison: { date_from: string; date_to: string };
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. Ta karta renderuje się raz na
   * szkołę ze zbieżnością, więc „Kontekst dostaw" czy „zmiana punktualności" powtarzają się
   * tyle razy, ile szkół weszło do wyniku — a nazwa szkoły w nagłówku zmienia się z oknem
   * czasowym, więc też nie jest kotwicą.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  return (
    <Paper variant="outlined" data-cc-card-id={kartaId} sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }} useFlexGap>
        <Chip size="small" color="warning" label="Zbieżność sprzedaż ↔ dostawy" />
        <DataQualityBadge quality={item.data_quality} reason={item.data_quality_reason} />
      </Stack>

      <Typography variant="h6" sx={{ mb: 1 }}>
        {item.school_name ?? `Szkoła #${item.school_id}`}
        {item.city ? ` · ${item.city}` : ''}
      </Typography>

      <Typography variant="overline" color="text.secondary">
        Fakt
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {/*
          „Sprzedaż spadła" bez podanej podstawy czyta się jak spadek PRZYCHODU. To są ceny
          katalogowe — przed zniżką szkolną — a kwota faktycznie pobrana bywa niższa i dla części
          transakcji w źródle nie istnieje. Karta była jedyną na pulpicie bez tej informacji,
          czyli jedynym miejscem, w którym liczba szła do właściciela bez swojej definicji.
        */}
        Wartość wg cennika spadła o {Math.abs(item.sales_delta_pct ?? 0)}% względem porównywalnego
        okna ({formatGrosze(item.sales_previous_grosze)} → {formatGrosze(item.sales_current_grosze)}).
      </Typography>
      <Box sx={{ mb: 1.5 }}>
        <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' }} />
      </Box>

      <Typography variant="overline" color="text.secondary">
        Kontekst dostaw
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Punktualność spadła z {item.on_time_previous_pct ?? '?'}% do {item.on_time_current_pct ?? '?'}%
        {item.avg_delta_change_min !== null
          ? `, a średnie odchylenie zmieniło się o ${item.avg_delta_change_min} min (teraz ${item.avg_delta_current_min} min).`
          : '.'}
      </Typography>

      <Typography variant="overline" color="text.secondary">
        Interpretacja
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Obie zmiany wystąpiły w tym samym oknie czasowym. To wszystko, co dane mówią.
      </Typography>

      <Typography variant="overline" color="text.secondary">
        Następny krok
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Sprawdzić, czy wzorzec utrzymuje się w kolejnym oknie i czy występuje w pozostałych szkołach
        tej samej restauracji. Jeśli tylko tutaj — problem jest lokalny.
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="overline" color="text.secondary">
        Na czym to stoi
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        <Row label="zamówienia (teraz / wcześniej)" value={`${formatCount(item.orders_current)} / ${formatCount(item.orders_previous)}`} />
        <Row label="dostawy w oknie" value={formatCount(item.deliveries)} />
        <Row label="zmiana punktualności" value={`${item.on_time_delta_points} p.p.`} />
        <Row label="zmiana sprzedaży" value={formatDelta(item.sales_delta_pct)} />
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
        <Typography variant="caption" color="text.secondary">
          Okno: {formatRange(window.date_from, window.date_to)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Porównanie: {formatRange(comparison.date_from, comparison.date_to)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Silnik: {CROSS_ENGINE_VERSION}
        </Typography>
      </Stack>

      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
        Związek przyczynowy nieustalony. Kierunek zależności może być odwrotny, przyczyna może być
        wspólna, a zbieżność może być przypadkiem.
      </Typography>
    </Paper>
  );
}

/** Raport złączenia — pozwala odróżnić „brak sygnału” od „nie da się policzyć”. */
export function JoinReportCard({ report }: { report: JoinReport }) {
  const color = report.status === 'READY' ? 'success' : report.status === 'PARTIAL' ? 'warning' : 'error';
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Wiarygodność złączenia</Typography>
        <Chip size="small" color={color} variant="outlined" label={report.status} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {report.reason}
      </Typography>
      <Row label="szkoły ze sprzedażą" value={formatCount(report.sales_schools)} />
      <Row label="szkoły z dostawami" value={formatCount(report.delivery_schools)} />
      <Row label="dopasowane" value={formatCount(report.matched)} />
      <Row label="tylko sprzedaż" value={formatCount(report.sales_only)} />
      <Row label="tylko dostawy" value={formatCount(report.delivery_only)} />
      <Row label="dostawy bez identyfikatora szkoły" value={formatCount(report.delivery_rows_without_id)} />
    </Paper>
  );
}
